import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/components/ui/skeleton';
import { Funnel, TermLabel } from '../../../../../components/design-system/analytics';
import { SectionCard } from '../../../../../components/design-system/admin';

export interface VizFunnelCounts {
  visitors: number | null;
  tried: number | null;
  saved: number | null;
  leads: number | null;
}

/**
 * The visualizer's four steps, in people: came, tried a product on a room,
 * kept the result, left their address. The step that loses the most is
 * marked by the Funnel itself.
 */
export function VizFunnel({ counts, loading, className }: { counts: VizFunnelCounts; loading?: boolean; className?: string }) {
  const { t } = useTranslation();
  const v = counts.visitors ?? 0;
  const share = (n: number | null) => (v > 0 && n != null ? t('VIZ_ANALYTICS.FUNNEL.OF_VISITORS', { percent: Math.round((n / v) * 100) }) : undefined);

  return (
    <SectionCard
      title={
        <TermLabel label={t('VIZ_ANALYTICS.FUNNEL.WHAT')} tip={t('VIZ_ANALYTICS.FUNNEL.TIP')}>
          {t('VIZ_ANALYTICS.FUNNEL.TITLE')}
        </TermLabel>
      }
      description={t('VIZ_ANALYTICS.FUNNEL.DESCRIPTION')}
      className={className}
      testId="viz-funnel"
    >
      {loading ? (
        <div className="flex flex-col gap-3">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      ) : (
        <Funnel
          stages={[
            { key: 'visited', label: t('VIZ_ANALYTICS.FUNNEL.VISITED'), value: v },
            { key: 'tried', label: t('VIZ_ANALYTICS.FUNNEL.TRIED'), value: counts.tried ?? 0, hint: share(counts.tried) },
            { key: 'saved', label: t('VIZ_ANALYTICS.FUNNEL.SAVED'), value: counts.saved ?? 0, hint: share(counts.saved) },
            { key: 'lead', label: t('VIZ_ANALYTICS.FUNNEL.LEFT_CONTACT'), value: counts.leads ?? 0, hint: share(counts.leads) },
          ]}
        />
      )}
    </SectionCard>
  );
}
